// ── Agent log parsing ───────────────────────────────────────────────────────
//
// The demo agent writes plain text lines shaped like:
//
//   2026-08-19T14:03:11.482Z [INFO] [Campaign] Realtime change detected
//
// The viewer filters on level, category and time, so these three parsers sit
// between a text blob and every filter on the page.

export const LOG_LEVELS = ['INFO', 'WARN', 'ERROR'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

/** ISO-8601 with a mandatory Z, anchored to the start of the line. */
export const ISO_TS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/;

export type DatePreset = 'all' | 'today' | '3d' | '7d' | '30d' | 'custom';

export const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: '3d', label: '3 days' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'custom', label: 'Custom' },
];

/**
 * Pull "[LEVEL] [Category]" out of a log line.
 *
 * Not anchored: the pair is found wherever it appears, which is what lets the
 * timestamp sit in front of it. `\w+` means a category with a space or hyphen
 * will not match and the line is treated as uncategorised.
 */
export function parseLogLine(line: string): { level: string; category: string } | null {
  const m = line.match(/\[(\w+)\]\s*\[(\w+)\]/);
  return m ? { level: m[1], category: m[2] } : null;
}

/**
 * Epoch milliseconds from the timestamp at the start of a line.
 *
 * Returns null for a continuation line - stack traces and wrapped output carry
 * no timestamp of their own.
 */
export function parseLogTimestamp(line: string): number | null {
  const m = line.match(ISO_TS_RE);
  if (!m) return null;
  const t = new Date(m[0]).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * The time window a preset selects, or null for no filtering.
 *
 * 'today' means since local midnight; the rolling presets are measured back
 * from now rather than snapped to day boundaries, so '3d' is 72 hours ago and
 * not the start of the day three days back.
 */
export function getPresetRange(
  preset: DatePreset,
  now: number = Date.now()
): { from: number; to: number } | null {
  if (preset === 'all') return null;
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  switch (preset) {
    case 'today': return { from: todayStart.getTime(), to: now };
    case '3d':    return { from: now - 3 * 86400_000, to: now };
    case '7d':    return { from: now - 7 * 86400_000, to: now };
    case '30d':   return { from: now - 30 * 86400_000, to: now };
    default:      return null;
  }
}
